import axios, {type AxiosInstance, type AxiosResponse} from 'axios';
import {clearTokens, getAccessToken, getRefreshToken, setTokens} from '@/lib/token-storage';

export const NEW_API_URL: string = import.meta.env.VITE_NEW_API_URL ?? '';

/** Sent on every backend call; the backend identifies the client by them. */
export const APP_HEADERS = {
  'X-App-Platform': 'web',
  'X-App-Name': 'sg.accumulus.android.atlas.enterprisedev',
  'X-App-Version': '1.0.15'
};

/**
 * The backend's `{success, message, data, error, error_message}` envelope.
 * Like the app's newAxiosClient, the client resolves failures as
 * `{error: true, ...body}` rather than rejecting, so callers read the
 * envelope (see `tnFailure`).
 */
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  error: boolean;
  error_message: string | null;
}

export interface ListMeta {
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
}

export interface ListResponse<T> {
  rows: T[];
  meta?: ListMeta;
}

/** Credential endpoints: a 401 there is a bad password, not an expired session. */
const isAuthEndpoint = (url?: string) => !!url && /^\/?auth\//.test(url);

let refreshing: Promise<string | null> | null = null;
const logoutListeners = new Set<() => void>();

/** Called when the refresh token is gone or rejected. */
export const onForcedLogout = (listener: () => void) => {
  logoutListeners.add(listener);
  return () => {
    logoutListeners.delete(listener);
  };
};

/** app: config/axios.ts — POST /auth/refresh-token with the refresh token. */
const refreshAccessToken = (): Promise<string | null> => {
  refreshing ??= (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return null;
    try {
      const res = await axios.post(
        `${NEW_API_URL}/auth/refresh-token`,
        {},
        {headers: {...APP_HEADERS, Authorization: `Bearer ${refreshToken}`}}
      );
      const tokens = res.data?.data ?? res.data;
      if (!tokens?.access_token) return null;
      setTokens(tokens.access_token, tokens.refresh_token ?? refreshToken);
      return tokens.access_token as string;
    } catch {
      return null;
    }
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
};

const createClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: NEW_API_URL,
    timeout: 1000 * 60 * 5,
    headers: APP_HEADERS
  });

  // Dev-only sample backend (src/mocks), for working without an ATC account.
  if (import.meta.env.DEV && import.meta.env.VITE_MOCK_API === '1') {
    client.defaults.adapter = async (config) => (await import('@/mocks/adapter')).mockAdapter(config);
  }

  client.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token && !config.headers.Authorization && !isAuthEndpoint(config.url)) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (response: AxiosResponse) => response?.data ?? response,
    async (error: any) => {
      const original = error?.config;
      const status = error?.response?.status;
      if (
        original &&
        [401, 403].includes(status) &&
        !original._retry &&
        !isAuthEndpoint(original.url) &&
        getRefreshToken()
      ) {
        original._retry = true;
        const token = await refreshAccessToken();
        if (token) {
          original.headers.Authorization = `Bearer ${token}`;
          return client.request(original);
        }
        clearTokens();
        logoutListeners.forEach((listener) => listener());
      }
      const body = error?.response?.data;
      if (body && typeof body === 'object') {
        return {error: true, status_code: status, ...body};
      }
      return {
        error: true,
        status_code: status,
        error_message: error?.message ?? 'Request failed'
      };
    }
  );

  return client;
};

export const apiClient = createClient();
